import { TilesState } from "@components/background/tiles/TilesState"
import { AwardEntryCard } from "@components/cards/AwardEntryCard"
import { Chip } from "@components/controls/Button"
import { HR } from "@components/layout/HR"
import { ResultList } from "@components/layout/ResultList"
import { StaggerTransitionRoot } from "@components/motion/Transition"
import { IconAwards, IconCaretDown, IconFilter } from "@components/svg/Icons"
import { AwardsFilter, MobileAwardsFilter, useAwardsSearch } from "@features/awards/components/AwardsFilter"
import { AwardsMobileSubnav } from "@features/awards/components/AwardsMobileSubnav"
import { useStudio } from "@hooks/queries"
import { useReduceMemo } from "@hooks/state-hooks"
import { SingleContentBlock } from "eddev/blocks"
import { defineView } from "eddev/views"
import { motion } from "framer-motion"
import { ReactNode, useMemo, useRef, useState } from "react"

export const meta: ViewMeta = {
  templateName: "Awards Listing",
}

let LAST_YEAR = 0

export default defineView("template-awards-listing", (props) => {
  const contentAreaRef = useRef<HTMLDivElement>(null)
  const search = useAwardsSearch({
    options: props.awardFilters!,
  })

  const { lookup, params, filter, hasFilters } = search

  const studioLookup = useStudio(
    {
      id: String(params.studio),
    },
    {
      enabled: !!params.studio,
    },
  )

  const header = useReduceMemo<ReactNode>(
    (last) => {
      let title: ReactNode
      let subtitle: ReactNode
      const categoryName = params.category && props.awardFilters?.categories?.find((c) => c?.slug === params.category)?.label
      if (lookup.isLoading && last) return last
      if (params.studio && !studioLookup.isLoading && studioLookup.data) {
        title = studioLookup.data.studio?.title!
        subtitle = categoryName
      } else if (params.year) {
        title = params.year
        subtitle = params.category?.replace("student-", "") && props.awardFilters?.categories?.find((c) => c?.slug === params.category?.replace("student-", ""))?.label
      }
      if (title || subtitle) {
        return (
          <>
            {title && <div className="type-title-l-medium">{title}</div>}
            {subtitle && (
              <div className="type-title-l-roman">
                {params.student ? "Student " : ""}
                {subtitle}
              </div>
            )}
          </>
        )
      }
      return null
    },
    [params, lookup.isLoading, studioLookup.isLoading, studioLookup.data],
  )

  const [mobileFilterOpen, setMobileFilterOpen] = useState(false)

  const mobileFilterLabel = useMemo(() => {
    const labels = []
    if (params.year) labels.push(params.year)
    if (params.category) {
      const cat = props.awardFilters?.categories?.find((c) => c?.slug === params.category)
      labels.push(cat?.label)
    }
    if (params.types) {
      const types = params.types.map((t) => props.awardFilters?.types?.find((c) => c?.slug === t)?.label)
      labels.push(types.join(", "))
    }
    if (params.q) labels.push(`"${params.q}"`)
    if (params.student) labels.push("Student")
    return labels.join(", ")
  }, [params])

  const ctaCards = useMemo(() => {
    if (params.year && !params.category && !params.q && !params.studio && !params.types) {
      return props.awards?.nodes.find((a) => a.info?.awardYear == params.year)?.contentBlocks
    }
  }, [params])

  const resultCategories = useMemo(() => {
    const childCategories: string[] = []
    lookup.data?.nodes.forEach((node) => {
      if (!childCategories.includes(node.category?.title!)) {
        childCategories.push(node.category!.title!)
      }
    })
    return childCategories
  }, [lookup.data?.nodes])

  const [activeSubcat, setActiveSubcat] = useState<string | null>(null)

  const results = useMemo(() => {
    if (!activeSubcat) return lookup.data?.nodes
    return lookup.data?.nodes.filter((node) => node.category?.title === activeSubcat)
  }, [lookup.data?.nodes, activeSubcat])

  return (
    <StaggerTransitionRoot>
      <div className="pb-8 min-h-[80vh]">
        {/* Mobile Filter */}
        <div className="w-grid mx-auto md:hidden flex flex-col gap-7">
          {/* <AwardsMobileSubnav /> */}

          <div className="bg-grey-light rounded-md">
            <button className="px-4 py-3 w-full flex items-center justify-between" onClick={() => setMobileFilterOpen(!mobileFilterOpen)}>
              <div className="type-title-xs-medium">{mobileFilterOpen ? "Filter" : mobileFilterLabel}</div>

              {mobileFilterOpen ? <IconCaretDown /> : <IconFilter />}
            </button>
            <motion.div
              className="overflow-hidden"
              variants={{ expanded: { height: "auto" }, collapsed: { height: 0 } }}
              initial="collapsed"
              animate={mobileFilterOpen ? "expanded" : "collapsed"}
            >
              <div className="border-t px-4 py-4">
                <MobileAwardsFilter
                  options={props.awardFilters!}
                  values={params}
                  onValueChange={(vals) => {
                    window.scrollTo({ top: 0, behavior: "instant" })
                    filter.setAll(vals)
                  }}
                  onClose={() => setMobileFilterOpen(false)}
                />
              </div>
            </motion.div>
          </div>
        </div>
        <div data-transition="children/fade" className="grid-auto">
          <div className="hidden md:block col-span-12 md:col-span-3">
            <div className="sticky top-headerHeight pt-7 md:min-h-pageMin pb-8 flex align-stretch">
              <AwardsFilter
                options={props.awardFilters!}
                values={params}
                onValueChange={(vals) => {
                  window.scrollTo({ top: 0, behavior: "instant" })
                  filter.setAll(vals)
                  setActiveSubcat(null)
                }}
              />
            </div>
          </div>
          <div ref={contentAreaRef} className="col-span-12 md:col-span-9 pt-7">
            {!!header && (
              <>
                {search.orderType === "year" ? (
                  <>
                    <div className="stack-x-2 items-start pb-1">
                      <div className="stack-y-1 flex-auto">{header}</div>
                      <div className="flex-none type-title-xs-roman">{lookup.data?.total! > 1 ? lookup.data?.total + " results" : ""}</div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="stack-x-2 items-end pb-4">
                      <div className="stack-y-1 flex-auto">{header}</div>
                      <div className="flex-none type-title-xs-roman">{lookup.data?.total! > 1 ? lookup.data?.total + " results" : ""}</div>
                    </div>
                    <HR />
                    {search.params?.category && resultCategories.length > 0 && (
                      <div className="w-full flex flex-wrap gap-1 mt-3">
                        {resultCategories.map((subcat, i) => (
                          <Chip
                            key={i}
                            active={subcat === activeSubcat ? true : false}
                            onClick={() => {
                              setActiveSubcat(subcat === activeSubcat ? null : subcat)
                            }}
                          >
                            {subcat}
                          </Chip>
                        ))}
                      </div>
                    )}
                    <div className="h-6" />
                  </>
                )}
              </>
            )}
            {ctaCards?.length! > 0 && (
              <div>
                <h3 className="type-title-s-medium mb-4">Overview</h3>
                <div className="grid-auto gap-y-4 mb-7 items-stretch">
                  {ctaCards?.map((block, i) => (
                    <div className="col-span-12 sm:col-span-6 md:col-span-3 flex items-stretch" key={i}>
                      <SingleContentBlock block={block} index={i} key={i} />
                    </div>
                  ))}
                </div>
              </div>
            )}

            <ResultList
              mode="card"
              items={results}
              component={AwardEntryCard}
              dynamicSizing={false}
              keyField="id"
              mobileMode="row"
              loading={lookup.isLoading}
              loadingNextPage={lookup.isFetchingNextPage}
              hasNextPage={lookup.hasNextPage}
              onFetchMore={() => lookup.fetchNextPage()}
              colSizes="col-span-12 sm:col-span-6 md:col-span-3 lg:col-span-3"
              getGroupHeading={(item) => {
                if (search.orderType === "year") {
                  return "year:" + item.year
                }
                if (!filter.get("category")) {
                  if (item.merit === "Winner") return "Distinction"
                }
                return item.merit!
              }}
              renderGroupHeading={(label) => {
                if (label.startsWith("year:")) {
                  return (
                    <div className="stack-x-2 items-center type-title-l-roman border-b pb-3">
                      <div>{label.replace("year:", "")}</div>
                    </div>
                  )
                } else {
                  return (
                    <div className="stack-x-2 items-center">
                      <IconAwards />
                      <div>{label}</div>
                    </div>
                  )
                }
              }}
            />
          </div>
        </div>
      </div>
    </StaggerTransitionRoot>
  )
})
